import React from "react";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
// icons
import FileIcon from "@material-ui/icons/InsertDriveFile";
import DeleteIcon from "@material-ui/icons/Cancel";

import Chip from "@material-ui/core/Chip";

import jsPDF from "jspdf";
// eslint-disable-next-line
import autoTable from "jspdf-autotable";

import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

import { showNotification } from "jazasoft";

import {
  toFixed,
  getDistinctValues,
  isEmpty,
  sizeKey as getSizeKey,
  listFromCsv,
  toFixedValue,
  getWeightedMean,
  isTrue
} from "../../../utils/helpers";
import { cbPartSort, getSizeRatio, parseMarkerName } from "../../../helpers/orderHelper";
import { setDefault, minReqHeight, totalPagesExp } from "../../../helpers/pdfHelper";
import { CUT_OFF_SIZE_RATIO, DEFAULT_SEPARATOR, PART_SHELL } from "../../../constants";
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import { useDeepCallback, useDeepMemo } from "../../../hooks";
import { CSV, EstimationState, FabricDirection, Role } from "../../../utils/types";
import { colorLightGreen, colorSecondary, colorTomatoRed } from "../../../utils/colors";
import { REGEX_REPEAT, REGEX_SHRINKAGE } from "../../../utils/regex";
import { regex } from "@jazasoft/mui-table";
import hasRole from "../../../utils/hasRole";
import { minValue, required } from "@jazasoft/mui-table";

const DialogName = {
  REVISION: "Revision",
  STYLE_DETAILS: "styleDetails",
  HISTORY: "history",
  SETTINGS: "settings",
  LINK_COSTING: "linkCosting",
  MINI_MARKER_UPLOAD: "miniMarkerUpload",
  MINI_MARKER_DELETE: "miniMarkerDelete"
};

const AnimatedTextField = ({ value, animate }) => {
  const useStyles = makeStyles(_ => ({
    animation: {
      backgroundColor: "turquoise",
      animation: "$blinker 1s linear infinite",
      padding: "0.25em 0"
    },
    "@keyframes blinker": {
      "50%": {
        opacity: 0
      }
    }
  }));
  const classes = useStyles();
  return <div className={clsx({ [classes.animation]: animate })}>{!isEmpty(value) ? value : "-"}</div>;
};

const renderStatus = value => (
  <div
    style={{
      backgroundColor:
        value === EstimationState.NEW || value === EstimationState.PENDING
          ? colorTomatoRed.dark
          : value === EstimationState.AVAILABLE
            ? colorSecondary.dark
            : colorLightGreen.dark,
      color: "white",
      padding: "4px 8px",
      borderRadius: 4,
      textAlign: "center"
    }}>
    {value}
  </div>
);

const parseMarker = (m, sizeKeys, idx) => ({
  ...m,
  markerNo: ++idx,
  name: sizeKeys
    .filter(sKey => m[getSizeKey(sKey)] !== "" && parseInt(m[getSizeKey(sKey)]) > 0)
    .map(sKey => `${sKey}:${m[getSizeKey(sKey)]}`)
    .join(", "),
  way: sizeKeys
    .filter(size => m[getSizeKey(size)] !== "" && parseInt(m[getSizeKey(size)]) > 0)
    .reduce((acc, size) => acc + parseInt(m[getSizeKey(size)]), 0)
});

const mapperMarker = (marker, sizeKeys, idx) => {
  const sizeRatio = getSizeRatio(marker.name, sizeKeys, getSizeKey);
  let plyCount, plyMap;
  try {
    if (marker.plies) {
      plyMap = JSON.parse(marker.plies);
      plyCount = Object.keys(plyMap).reduce((acc, color) => acc + plyMap[color], 0);
    }
  } catch (error) {
    console.log(error);
  }

  return {
    ...marker,
    ...sizeRatio,
    name: parseMarkerName(marker.name)?.name,
    markerNo: `M${marker.markerNo || idx + 1}`,
    length: marker.length || "",
    efficiency: marker.efficiency || "",
    remarks: marker.remarks || "",
    qtyPercent: parseFloat(toFixed(marker.qtyPercent, 2)),
    plyCount,
    plyMap
  };
};

const topSummaryParameters = ["orderingCu,Ordering CU", "markerEfficiency,Marker Efficiency", "maxWay,Max Way"];
const variableParameters = [
  "width,Width (CM)",
  "repeat,Repeat",
  "shrinkage,Shrinkage",
  "fabDirection,Fab. Direction",
  "matchingParameter,Matching Parameter",
  "csv,CSV"
];
const bottomSummaryParameters = ["reason,Reason", "remarks,Remarks", "updatedAt,Date", "finalizedOption,Finalized Option"];

const useEstimationView = ({ id, roles, history }) => {
  const [version, setVersion] = React.useState(1); // For Refreshing estimation data
  const [flowIdx, setFlowIdx] = React.useState(0); // Estimation Flow Tab
  const [revIdx, setRevIdx] = React.useState(0); // Tentative/Final Tab
  const [summaryIdx, setSummaryIdx] = React.useState(0);
  const [estimation, setEstimation] = React.useState({}); // Store Estimation in state to reduce extra fetch
  const [dialogState, setDialogState] = React.useState({ active: false, name: null, part: null }); // name: Revision, Settings, Style Details, Link Costing, History, Mini Marker Upload, Mini Marker Delete
  const [cuChanged, setCuChanged] = React.useState({ part: null, cuType: null }); // Part - Part of which ordering Cu or tentative lay Cu has changed on marker update, cuType - ordering, tentative
  const [openFinalizeDialog, setOpenFinalizeDialog] = React.useState(false); // Flag to open finalize dialog
  const [isAttachmentUpload, setIsAttachmentUpload] = React.useState(false);

  const [drawerState, setDrawerState] = React.useState({ open: false, rows: [], columns: [] });

  const dispatch = useDispatch();
  const isEstCad = hasRole(roles, [Role.EST_CAD]);

  // api calls
  const { data: estData = {}, loading: eLoading } = useQuery({ url: `estimations/${id}`, version });
  const { data: fabricPatternList = [] } = useQuery({ url: "fabric-patterns", params: { size: 100 } }, ({ id, name } = {}) => ({
    id: `${id}`,
    name
  }));
  const { data: techSheets = "" } = useQuery(
    estimation && estimation?.cuChecklistId && { url: `cuChecklists/${estimation?.cuChecklistId}` },
    ({ techSheets }) => techSheets
  );

  const choicesTechSheets = techSheets ? techSheets.split(",").map(e => ({ id: e, name: e })) : [];

  const [mutate, { loading: loading1 }] = useMutation();
  const [download, { loading: loading2 }] = useMutation({ rawResponse: true });

  const busy = loading1 || loading2 || eLoading;
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    // Detecting if ordering CU has changed for any part
    let cuChanged = { part: null, cuType: null };
    if (!isEmpty(estimation) && !isEmpty(estData) && estimation.parts) {
      estimation.parts.forEach(p => {
        let newEstPart = estData.parts.find(e => e.part === p.part);
        if (p.layCu !== newEstPart?.layCu) {
          cuChanged.part = p.part;
          cuChanged.cuType = "ordering";
        } else if (p.tentativeLayCu !== newEstPart.tentativeLayCu) {
          cuChanged.part = p.part;
          cuChanged.cuType = "tentative";
        }
      });
    }
    setEstimation(estData);
    setCuChanged(cuChanged);
  }, [busy, eLoading, setEstimation]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const flowList = estimation.flowList?.sort((a, b) => (a.colors < b.colors ? -1 : 1)) || [];
  const activeFlow = flowList[flowIdx];
  const colorList = activeFlow?.colors?.split(",") || [];

  const revisionList = JSON.parse(activeFlow?.revisionDetail || "[]");
  const revisions = getDistinctValues(revisionList?.map(r => r.revisionNo))?.sort((a, b) => a - b) || [];
  const choicesRevisions = revisions.map(revNo => ({ id: revNo, name: `Estimation ${revNo}` }));

  const manuallyFinalizedRevNo =
    revisionList?.find(r => !isEmpty(r.manualFinalizedOption) && isTrue(r.manualFinalizedOption))?.revisionNo || undefined;

  const onEstimationUpdate = values => {
    if (dialogState.name === DialogName.STYLE_DETAILS) {
      const message = "Style Details updated successfully.";
      updateEstimation({ ...estimation, ...values }, message);
    } else if (dialogState.name === DialogName.LINK_COSTING) {
      const message = "Costing linked successfully.";
      const restParts = estimation.parts?.filter(p => dialogState.estPart?.id !== p.id) || [];
      let data = {
        ...estimation,
        parts: [...restParts, { ...dialogState.estPart, costingId: values.costingId }]
      };
      updateEstimation(data, message);
    }
    setDialogState({});
  };

  const onFinalizeDialogClose = () => {
    setOpenFinalizeDialog(false);
  };

  // Process Parts data
  const columnsPart = useDeepMemo(() => {
    const cols = [
      { dataKey: "state", title: "Status", render: renderStatus },
      { dataKey: "color", title: "Color" },
      { dataKey: "part", title: "Part" },
      {
        dataKey: "placement",
        title: "Placement",
        inputType: "text-input",
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad
      },
      { dataKey: "fabArticle", title: "Article" },
      { dataKey: "supplier", title: "Supplier", render: value => value?.name },
      { dataKey: "fabricCategory", title: "Fab. Category", render: value => value?.name },
      {
        dataKey: "fabricPatternId",
        title: "Fab. Pattern",
        inputType: "select-input",
        render: (value, shortValue, row) => row?.fabricPattern?.name,
        choices: fabricPatternList,
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad
      },
      {
        dataKey: "width",
        title: "Width (CM)",
        inputType: "text-input",
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad,
        options: { type: "number" }
      },
      {
        dataKey: "repeat",
        title: "Repeat",
        inputType: "text-input",
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad,
        validate: regex(REGEX_REPEAT, "Invalid format. E.g - 1.5 X 2.5 or 1.5")
      },
      {
        dataKey: "shrinkage",
        title: "Shrinkage",
        inputType: "text-input",
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad,
        validate: regex(REGEX_SHRINKAGE, "Invalid format. E.g - L1.5% W2.5%")
      },
      {
        dataKey: "fabDirection",
        title: "Fab. Direction",
        inputType: "select-input",
        choices: Object.keys(FabricDirection).map(key => ({ id: FabricDirection[key], name: FabricDirection[key] })),
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad
      },
      {
        dataKey: "matchingParameter",
        title: "Matching Parameter",
        inputType: "text-input",
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad
      },
      {
        dataKey: "csv",
        title: "CSV",
        inputType: "select-input",
        choices: Object.values(CSV).map(e => ({ id: e, name: e })),
        disabled: row => row?.state === EstimationState.COMPLETED || row?.state === EstimationState.AVAILABLE || isEstCad
      },
      { dataKey: "costingCuByCad", title: "Costing CU\nby CAD", render: value => (isEmpty(value) ? "-" : Number(value).toFixed(3)) },
      { dataKey: "wastageBuffer", title: "Wastage &\nBuffer (%)", render: value => (isEmpty(value) ? "-" : toFixedValue(value, 3)) },
      { dataKey: "finalCostingCu", title: "Final Costing\nCU", render: value => (isEmpty(value) ? "-" : Number(value).toFixed(3)) },
      {
        dataKey: "layCu",
        title: "Ordering CU\nBy CAD",
        align: "center",
        render: (value, shortValue, row) => (
          <AnimatedTextField
            value={isEmpty(value) ? "-" : Number(value).toFixed(3)}
            animate={row.part === cuChanged.part && cuChanged.cuType === "ordering"}
          />
        )
      },
      {
        dataKey: "wastage",
        title: "Wastage (%)",
        align: "right",
        inputType: "text-input",
        options: { type: "number" },
        disabled: () => !hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER, Role.MERCHANT, Role.EST_CAD, Role.CUT_PLANNER]),
        render: value => (isEmpty(value) ? "-" : value)
      },
      {
        dataKey: "orderingCu",
        title: "Final Ordering CU",
        align: "right",
        render: value => (isEmpty(value) ? "-" : Number(value).toFixed(3))
      },
      {
        dataKey: "orderQty",
        title: "Order Qty",
        align: "right"
      },
      {
        dataKey: "extra",
        title: "Extra (%)",
        align: "right",
        inputType: "text-input",
        disabled: () => !hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER, Role.MERCHANT, Role.EST_CAD, Role.CUT_PLANNER]),
        options: { type: "number" }
      },
      { dataKey: "fabRequired", title: "Fab. Required", align: "right", render: value => (isEmpty(value) ? "-" : `${toFixed(value, 0)} m`) },
      {
        dataKey: "cstId",
        title: "Costing",
        render: (value, shortValue, row) => row?.costing?.cstId || "-",
        linkPath: row => row?.costingId && history.push(`/estimation/costings/${row.costingId}/view`)
      }
    ];
    return cols;
  }, [estimation]);

  const rowsPart = useDeepMemo(() => {
    if (isEmpty(estimation?.parts) || isEmpty(activeFlow)) return [];
    const colorList = listFromCsv(activeFlow.colors);

    const revNo = revIdx + 1;
    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
    const revisionList = revisionDetailList.filter(e => e.revisionNo === revNo);

    const partList = estimation.parts.filter(p => colorList.includes(p.color)).sort(cbPartSort) || [];
    return partList
      .map(p => ({ ...p, revision: revisionList.find(rev => rev.part === p.part) }))
      .map(p => {
        const shellRevision = revisionList.find(e => e.part === PART_SHELL);
        const sizeBreakupList = shellRevision?.sizeBreakupList || [];
        const isSizeRatio = !isEmpty(sizeBreakupList) && sizeBreakupList.every(sb => sb.qty <= CUT_OFF_SIZE_RATIO);
        const qty = sizeBreakupList.filter(sb => sb.color === p.color).reduce((acc, sb) => acc + sb.qty, 0);
        const orderQty = isSizeRatio ? shellRevision?.orderQty : qty;
        const extra = p.revision?.consumptionList?.find(c => c.color === p.color)?.extra || 0;
        const orderingCu = p.revision?.consumptionList?.find(c => c.color === p.color)?.orderingCu || 0;
        const fabRequired = orderingCu > 0 ? orderQty * (100 + extra) * 0.01 * orderingCu : undefined;

        return {
          ...p,
          ...p.revision,
          orderQty,
          costingCuByCad: toFixed(p?.cstPart?.layCu, 3),
          wastageBuffer: toFixed(p?.cstPart?.wastage, 2),
          finalCostingCu: toFixed(p?.cstPart?.costingCu, 3),
          layCu: toFixed(p.revision?.consumptionList?.find(c => c.color === p.color)?.layCu, 3),
          wastage: toFixed(p.revision?.consumptionList?.find(c => c.color === p.color)?.wastage, 3),
          orderingCu: toFixed(p.revision?.consumptionList?.find(c => c.color === p.color)?.orderingCu, 3),
          extra,
          fabRequired: toFixed(fabRequired, 2),
          totalRevisionNo: Math.max(0, ...revisionDetailList.map(e => e.revisionNo))
        };
      });
  }, [estimation, activeFlow, revIdx]);

  const partList = getDistinctValues(rowsPart.map(e => e.part));

  const onPartSubmit = (rows, form, onSubmitComplete) => {
    const uniqueParts = getDistinctValues(rows.map(e => `${e.part}_${e.color}`));
    const revisionList = uniqueParts.map(part => {
      const pList = rows.filter(r => `${r.part}_${r.color}` === part);

      const consumptionList = pList.map(p => ({
        color: p.color,
        wastage: Number(p.wastage),
        extra: Number(p.extra)
      }));

      return {
        state: pList[0]?.state,
        part: pList[0]?.part,
        placement: pList[0]?.placement,
        fabricPatternId: pList[0]?.fabricPatternId,
        width: pList[0]?.width,
        repeat: pList[0]?.repeat,
        shrinkage: pList[0]?.shrinkage,
        fabDirection: pList[0]?.fabDirection,
        matchingParameter: pList[0]?.matchingParameter,
        csv: pList[0]?.csv,
        consumptionList
      };
    });

    const options = {
      url: `estimations/${id}/revisions`,
      method: "patch",
      data: revisionList,
      params: { flowId: activeFlow?.id, revNo: revIdx + 1, action: "size_breakup" }
    };

    mutate(options, {
      onSuccess: data => {
        setVersion(version => version + 1);
        dispatch(showNotification("Size Breakup updated successfully."));
        onSubmitComplete(rows);
      },
      onFailure: () => {
        onSubmitComplete(rows);
      }
    });
  };

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (action === "link" && row.part === PART_SHELL) {
      setDialogState({ name: DialogName.LINK_COSTING, active: true, estPart: row });
    }
  };

  const columnsSizeBreakup = useDeepMemo(() => {
    if (isEmpty(estimation?.sizeBreakup) || isEmpty(activeFlow.revisionDetail)) return [];
    // const sbList = JSON.parse(estimation.sizeBreakup);
    // const sizeKeyList = getDistinctValues(sbList.sort((a, b) => a.serial - b.serial).map(e => e.sizeKey));

    const revNo = revIdx + 1;
    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);

    const shellRevisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);
    const sizeBreakupList = shellRevisionDetail?.sizeBreakupList || [];
    const isSizeRatio = !isEmpty(sizeBreakupList) && sizeBreakupList.every(sb => sb.qty <= CUT_OFF_SIZE_RATIO);
    const sizeKeyList = sizeBreakupList.sort((a, b) => a.serial - b.serial).map(e => e.sizeKey);

    const cols = [
      { dataKey: "color", title: "Color" },
      ...sizeKeyList.map(sKey => ({
        dataKey: getSizeKey(sKey),
        title: sKey.split("_")[1],
        inputType: "text-input",
        options: { type: "number" }
      })),
      {
        dataKey: "total",
        title: "Total",
        inputType: isSizeRatio ? "text-input" : undefined,
        options: { type: "number" }
      }
    ];
    return cols;
  }, [estimation, activeFlow, revIdx]);

  const rowsSizeBreakup = useDeepMemo(() => {
    if (isEmpty(activeFlow?.revisionDetail)) return [];

    const revNo = revIdx + 1;
    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
    const revisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);

    if (isEmpty(revisionDetail?.sizeBreakupList)) return [];
    const sizeBreakupList = revisionDetail?.sizeBreakupList || [];
    const isSizeRatio = !isEmpty(sizeBreakupList) && sizeBreakupList.every(sb => sb.qty <= CUT_OFF_SIZE_RATIO);

    const colorList = getDistinctValues(sizeBreakupList.map(e => e.color));
    const rows = colorList.map(color => {
      const qty = sizeBreakupList.filter(e => e.color === color).reduce((acc, sb) => acc + sb.qty, 0);
      return {
        color,
        total: isSizeRatio ? revisionDetail?.orderQty : qty,
        ...sizeBreakupList.filter(e => e.color === color).reduce((acc, sb) => ({ ...acc, [getSizeKey(sb.sizeKey)]: sb.qty }), {})
      };
    });

    return rows;
  }, [activeFlow, revIdx]);

  const onSizeBreakupSubmit = (rows, form, onSubmitComplete) => {
    if (isEmpty(estimation?.sizeBreakup) || isEmpty(activeFlow.revisionDetail)) {
      onSubmitComplete(rows);
      return;
    }
    // const sbList = JSON.parse(estimation.sizeBreakup);

    const revNo = revIdx + 1;
    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
    const shellRevisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);
    const sbList = shellRevisionDetail?.sizeBreakupList || [];
    const sizeKeyList = getDistinctValues(sbList.sort((a, b) => a.serial - b.serial).map(e => e.sizeKey));

    const sizeBreakupList = rows.flatMap(row =>
      sizeKeyList.map((sKey, idx) => ({
        color: row.color,
        serial: idx + 1,
        sizeGroup: sKey.split("_")[0],
        size: sKey.split("_")[1],
        qty: row[getSizeKey(sKey)] || 0
      }))
    );
    const isSizeRatio = !isEmpty(sizeBreakupList) && sizeBreakupList.every(sb => sb.qty <= CUT_OFF_SIZE_RATIO);
    const orderQty = isSizeRatio
      ? rows.reduce((acc, row) => acc + Number(row.total || 0), 0)
      : sizeBreakupList.reduce((acc, sb) => acc + Number(sb.qty || 0), 0);

    const options = {
      url: `estimations/${id}/revisions`,
      method: "put",
      data: { orderQty, sizeBreakupList },
      params: { flowId: activeFlow?.id, revNo: revIdx + 1, action: "size_breakup" }
    };

    mutate(options, {
      onSuccess: data => {
        setVersion(version => version + 1);
        dispatch(showNotification("Size Breakup updated successfully."));
        onSubmitComplete(rows);
      },
      onFailure: () => {
        onSubmitComplete(rows);
      }
    });
  };

  const onDrawerSubmit = (sizeBreakupList, orderQty, onActionComplete) => {
    const options = {
      url: `estimations/${id}/revisions`,
      method: "put",
      data: { orderQty, sizeBreakupList },
      params: { flowId: activeFlow?.id, revNo: revIdx + 1, action: "size_breakup" }
    };

    mutate(options, {
      onSuccess: data => {
        setVersion(version => version + 1);
        dispatch(showNotification("Size Breakup updated successfully."));
        onActionComplete && onActionComplete([]);
        setDrawerState({ open: false, rows: [], columns: [] });
      },
      onFailure: () => {
        onActionComplete && onActionComplete([]);
        setDrawerState({ open: false, rows: [], columns: [] });
      }
    });
  };

  const updateEstimation = (estimation, message) => {
    const options = {
      url: `estimations/${id}`,
      method: "put",
      data: estimation
    };

    mutate(options, {
      onSuccess: () => {
        setVersion(version => version + 1);
        dispatch(showNotification(message));
      }
    });
  };

  const getColumnsMarker = useDeepCallback(
    isEditable => {
      if (isEmpty(activeFlow?.revisionDetail)) return [];

      const revNo = revIdx + 1;
      const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
      const revisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);
      const sizeBreakupList = revisionDetail?.sizeBreakupList?.filter(sb => sb.qty > 0) || [];
      const sizeKeys = getDistinctValues(sizeBreakupList.sort((a, b) => a.serial - b.serial).map(sb => sb.sizeKey));

      const cols = [
        { dataKey: "markerNo", title: "Marker No" },
        { dataKey: "name", title: "Marker" },
        ...sizeKeys.map(sKey => ({
          dataKey: getSizeKey(sKey),
          title: sKey.split("_")[1],
          inputType: "text-input",
          align: "center",
          options: { type: "number", style: { width: 120 } }
        })),
        { dataKey: "way", title: "Way" },
        { dataKey: "plyCount", title: "Plies" },
        { dataKey: "qty", title: "Qty" },
        {
          dataKey: "qtyPercent",
          title: "Qty (%)",
          render: value => `${toFixed(value, 1)}%`,
          inputType: "text-input",
          options: { type: "number" },
          validate: [required(), minValue(0.01, "Must be greater than zero")]
        },
        {
          dataKey: "length",
          title: "Length",
          inputType: "text-input",
          options: { type: "number" },
          validate: [required(), minValue(0.01, "Must be greater than zero")]
        },
        {
          dataKey: "efficiency",
          title: "Efficiency",
          inputType: "text-input",
          options: { type: "number" },
          validate: [required(), minValue(0.01, "Must be greater than zero")]
        },
        { dataKey: "remarks", title: "Remarks", inputType: "text-input" },
        {
          dataKey: "miniMarker",
          title: "Mini Marker",
          render: (filename, shortValue, row) =>
            filename ? (
              <Chip
                label={filename}
                variant="outlined"
                icon={<FileIcon />}
                onClick={() => onDownloadMiniMarker({ filename, part: row.part })}
                deleteIcon={isEditable ? <DeleteIcon /> : <React.Fragment />}
                onDelete={() =>
                  isEditable &&
                  setDialogState({
                    name: DialogName.MINI_MARKER_DELETE,
                    active: true,
                    filename,
                    part: row.part
                  })
                }
              />
            ) : (
              ""
            )
        }
      ];

      return cols;
    },
    [activeFlow, revIdx]
  );

  const rowsMarker = useDeepMemo(() => {
    if (isEmpty(activeFlow?.revisionDetail)) return [];

    const revNo = revIdx + 1;
    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
    const revisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);
    const sizeBreakupList = revisionDetail?.sizeBreakupList?.filter(sb => sb.qty > 0) || [];
    const sizeKeys = getDistinctValues(sizeBreakupList.sort((a, b) => a.serial - b.serial).map(sb => sb.sizeKey));

    const markers = activeFlow?.markers?.filter(m => m.revisionNumber === revIdx + 1) || [];
    return markers.sort((a, b) => a?.markerNo - b?.markerNo)?.map((marker, idx) => mapperMarker(marker, sizeKeys, idx));
  }, [activeFlow, revIdx]);

  const onMarkerSubmit = part => (markers, form, onSubmitComplete) => {
    if (!isAttachmentUpload) {
      if (isEmpty(activeFlow?.revisionDetail)) return;

      const revNo = revIdx + 1;
      const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
      const revisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);
      const sizeBreakupList = revisionDetail?.sizeBreakupList?.filter(sb => sb.qty > 0) || [];
      const sizeKeys = getDistinctValues(sizeBreakupList.sort((a, b) => a.serial - b.serial).map(sb => sb.sizeKey));

      const markerList = markers.map((m, idx) => parseMarker(m, sizeKeys, idx)).filter(m => !isEmpty(m.name));

      const options = {
        url: `estimations/${id}/markers`,
        method: "put",
        params: { flowId: activeFlow?.id, revNo: revIdx + 1, part },
        data: markerList
      };
      mutate(options, {
        onSuccess: data => {
          if (Array.isArray(data)) {
            //          onSubmitComplete(data?.map(marker => mapperMarker(marker, sizeList)).sort((a, b) => b.qtyPercent - a.qtyPercent));
            onSubmitComplete(data?.sort((a, b) => a.markerNo - b.markerNo)?.map(marker => mapperMarker(marker, sizeKeys)));
            setVersion(version => version + 1);
            dispatch(showNotification("Markers updated successfully."));
          }
        },
        onFailure: () => onSubmitComplete()
      });
    }

    if (isAttachmentUpload) {
      const markerList = markers.map(m => ({ id: m.id, miniMarker: m.attachment?.name }));

      const data = new FormData();
      markers.forEach(m => {
        if (!isEmpty(m.attachment)) {
          data.append("files", m.attachment);
        }
      });

      const options = {
        url: `estimations/${id}/miniMarkers`,
        method: "post",
        data,
        params: { estimationId: id, flowId: activeFlow?.id, part, revNo: revIdx + 1, body: JSON.stringify(markerList) },
        headers: { "Content-Type": "multipart/form-data" }
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Mini markers uploaded successfully"));
          setVersion(version + 1);
          setIsAttachmentUpload(false);
        },
        onFailure: () => {
          setIsAttachmentUpload(false);
          onSubmitComplete && onSubmitComplete(markers);
        }
      });
    }
  };

  const onFooterActionClick = (part, revisionDetail, isSizeRatio) => (event, action, rows, filterValues, onActionComplete) => {
    if (action === "setting") {
      setDialogState({ active: true, name: DialogName.SETTINGS, part, revisionDetail, isSizeRatio, onActionComplete });
    } else if (action === "run") {
      const options = {
        url: `estimations/${id}/markers`,
        method: "post",
        params: { flowId: activeFlow?.id, part, revNo: revIdx + 1 }
      };

      mutate(options, {
        onSuccess: () => {
          setVersion(version => version + 1);
          onActionComplete && onActionComplete([]);
          dispatch(showNotification("Markers generated successfully."));
        },
        onFailure: e => {
          onActionComplete && onActionComplete(rows);
        }
      });
    } else if (action === DialogName.MINI_MARKER_UPLOAD) {
      setIsAttachmentUpload(true);
    } else if (action === "delete") {
      const options = {
        url: `estimations/${id}/markers`,
        method: "delete",
        params: { flowId: activeFlow?.id, part, revNo: revIdx + 1 }
      };

      mutate(options, {
        onSuccess: () => {
          setVersion(version => version + 1);
          onActionComplete && onActionComplete([]);
        },
        onFailure: e => {
          onActionComplete && onActionComplete(rows);
        }
      });
    } else if (action === "cancel") {
      setIsAttachmentUpload(false);
      onActionComplete && onActionComplete(rows);
    }
  };

  const updateRevisionSetting = values => {
    const options = {
      url: `estimations/${id}/revisions`,
      method: "put",
      data: {
        ...values,
        part: dialogState?.part,
        cutSetting: { cutPlanType: values.cutPlanType, maxPieces: values.maxPieces, discardedWays: values.discardedWays }
      },
      params: { flowId: activeFlow?.id, revNo: revIdx + 1, action: "setting" }
    };

    mutate(options, {
      onSuccess: data => {
        setDialogState({});
        setVersion(version => version + 1);
        dispatch(showNotification("Setting updated successfully."));
        dialogState.onActionComplete && dialogState.onActionComplete([]);
      },
      onFailure: e => {
        setDialogState({});
        dialogState.onActionComplete && dialogState.onActionComplete([]);
      }
    });
  };

  const onDownloadMiniMarker = ({ filename, part }) => {
    const options = {
      url: `estimations/${id}/miniMarkers`,
      method: "get",
      params: { flowId: activeFlow?.id, revNo: revIdx + 1, part, filename },
      responseType: "arraybuffer"
    };
    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var file = header ? header.match(/filename="(.+)"/)[1] : filename;
          let blob = new Blob([response.data], { type: "octet/stream" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = file;
          link.click();
        }
      }
    });
  };

  const onDeleteMiniMarker = () => {
    const options = {
      url: `estimations/${id}/miniMarkers`,
      method: "delete",
      params: { ...dialogState, flowId: activeFlow?.id, revNo: revIdx + 1 }
    };
    mutate(options, {
      onSuccess: () => {
        setVersion(version + 1);
        setDialogState({});
        setIsAttachmentUpload(false);
        dispatch(showNotification("Mini Marker deleted successfully."));
      }
    });
  };

  const onCloseDialog = () => {
    const { onActionComplete = () => {}, rows = [] } = dialogState;
    setDialogState({});
    onActionComplete(rows);
  };

  const onRevision = values => {
    const options = {
      url: `estimations/${id}/revisions`,
      method: "post",
      data: values,
      params: { flowId: activeFlow?.id }
    };

    mutate(options, {
      onSuccess: data => {
        setDialogState({});
        setVersion(version => version + 1);
        dispatch(showNotification("New Revision created successfully."));
      },
      onFailure: e => {
        setDialogState({});
      }
    });
  };

  const onRevisionDelete = (flowId, revNo) => {
    const options = {
      url: `estimations/${id}/revisions`,
      method: "delete",
      params: { flowId, revNo }
    };

    mutate(options, {
      onSuccess: data => {
        setDialogState({});
        setVersion(version => version + 1);
        dispatch(showNotification("Revision deleted successfully."));
        setRevIdx(0);
      },
      onFailure: e => {
        setDialogState({});
      }
    });
  };

  const onFinalizeSubmit = values => {
    if (isEmpty(values?.revisionNo)) {
      dispatch(showNotification(`Revision No is required for finalizing Costing`, { type: "danger" }));
      return;
    }

    const options = {
      url: `estimations/${id}/revisions`,
      method: "put",
      params: { flowId: activeFlow?.id, revNo: values?.revisionNo, action: "finalize" },
      data: estimation
    };

    mutate(options, {
      onSuccess: data => {
        setVersion(version => version + 1);
        setOpenFinalizeDialog(false);
        dispatch(showNotification("Revision Finalized successfully."));
      },
      onFailure: e => {
        setOpenFinalizeDialog(false);
      }
    });
  };

  const onDownloadTechSheet = filename => {
    const options = {
      url: `estTechSheets`,
      method: "get",
      params: { filename, cuChecklistId: estData?.cuChecklistId },
      responseType: "arraybuffer"
    };
    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          let blob = new Blob([response.data], { type: "octet/stream" }),
            link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
      }
    });
  };

  //// Process Costing Summary data ////
  const columnsSummary = useDeepMemo(() => {
    if (isEmpty(activeFlow)) return [];
    const revisionCount = activeFlow.revisionCount || 1;
    if (revisionCount <= 1) return [];

    return [
      { dataKey: "parameter", title: "Parameter", length: 32 },
      ...Array(revisionCount)
        .fill("")
        .map((_, idx) => ({ dataKey: `revision${idx + 1}`, title: `Estimation ${idx + 1}` }))
    ];
  }, [activeFlow]);

  const rowsSummaryMap = useDeepMemo(() => {
    if (isEmpty(activeFlow) || isEmpty(estimation.parts)) return [];
    const revisionCount = activeFlow.revisionCount || 1;
    if (revisionCount <= 1) return [];

    const colorList = listFromCsv(activeFlow.colors);
    const partList = estimation.parts.filter(p => colorList.includes(p.color));

    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);

    const rowsSummaryMap = partList.reduce((acc, p) => {
      const color = p.color;
      const part = p.part;
      let partRows = [];

      const revisionList = revisionDetailList?.filter(e => e.part === p.part) || [];

      //Top Parameters
      topSummaryParameters.forEach(e => {
        const key = e.split(",")[0];
        const label = e.split(",")[1];
        if (key === "orderingCu") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            const consumption = rev?.consumptionList?.find(c => c.color === p.color);
            row[`revision${rev.revisionNo}`] = !isEmpty(consumption?.orderingCu) ? toFixed(consumption?.orderingCu, 3) : "-";
          });
          partRows.push(row);
        }
        if (key === "markerEfficiency") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            const markers =
              activeFlow?.markers?.filter(m => m.revisionNumber === rev.revisionNo && m.part === part).filter(m => !isEmpty(m.efficiency)) || [];
            row[`revision${rev.revisionNo}`] = isEmpty(markers)
              ? "-"
              : `${toFixed(getWeightedMean(markers.map(m => ({ key: m.efficiency, weight: m.qtyPercent ?? 100 }))), 2)}%`;
          });
          partRows.push(row);
        }
        if (key === "maxWay") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            const markers = activeFlow?.markers?.filter(m => m.revisionNumber === rev.revisionNo && m.part === part);
            const wayMarkers = markers?.map(m => m.way).filter(e => !isEmpty(e));
            row[`revision${rev.revisionNo}`] = wayMarkers?.length > 0 ? Math.max(...wayMarkers) : "-";
          });
          partRows.push(row);
        }
      });

      variableParameters.forEach(e => {
        const key = e.split(",")[0];
        const label = e.split(",")[1];
        const hasChanged = getDistinctValues(revisionList.map(e => e[key])).length > 1;
        if (hasChanged) {
          const row = revisionList.reduce((acc, rev) => ({ ...acc, [`revision${rev.revisionNo}`]: rev[key] }), { parameter: label });
          partRows.push(row);
        }
      });

      // Size Breakup
      const revList = revisionList.map(rev => ({
        ...rev,
        total: rev.sizeBreakupList?.filter(sb => sb.color === p.color).reduce((acc, sb) => acc + sb.qty, 0)
      }));
      const hasOrderQtyChanged = getDistinctValues(revList.map(rev => rev.orderQty || rev.total)).length > 1;
      if (hasOrderQtyChanged) {
        const row = revList.reduce((acc, rev) => ({ ...acc, [`revision${rev.revisionNo}`]: rev.orderQty || rev.total }), {
          parameter: "Order Qty"
        });
        partRows.push(row);
      }

      //Bottom Parameters
      bottomSummaryParameters.forEach(e => {
        const key = e.split(",")[0];
        const label = e.split(",")[1];

        if (key === "reason") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            row[`revision${rev.revisionNo}`] = rev.reason || "-";
          });
          partRows.push(row);
        }
        if (key === "remarks") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            row[`revision${rev.revisionNo}`] = rev.remarks || "-";
          });
          partRows.push(row);
        }
        if (key === "updatedAt") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            row[`revision${rev.revisionNo}`] = !isEmpty(rev.updatedAt) ? format(new Date(rev.updatedAt), "MMM dd, yy HH:mm aa") : "-";
          });
          partRows.push(row);
        }
        if (key === "finalizedOption") {
          let row = { parameter: label };
          revisionList.forEach(rev => {
            row[`revision${rev.revisionNo}`] = !isEmpty(rev?.manualFinalizedOption)
              ? isTrue(rev?.manualFinalizedOption)
                ? "Selected"
                : "Not Selected"
              : isTrue(rev?.systemFinalizedOption)
                ? "Selected"
                : "Not Selected";
          });
          partRows.push(row);
        }
      });

      return { ...acc, [`${color}${DEFAULT_SEPARATOR}${part}`]: partRows };
    }, {});

    return rowsSummaryMap;
  }, [estimation, activeFlow]);

  const onFlowChange = idx => {
    setFlowIdx(idx);
    setSummaryIdx(0);
    setRevIdx(0);
  };

  const onPrint = () => {
    if (!estimation) return;
    var doc = new jsPDF("l");
    setDefault(doc, "");
    doc.setFontSize(10);
    const pageHeight = doc.internal.pageSize.getHeight();

    let startY = 24;

    const heading = `Fabric Estimation for  ${estimation.customer?.name} #${estimation.poRef}`;
    doc.text(heading, doc.internal.pageSize.getWidth() / 2, 15, "center");

    // Print Order Details
    const headerOdrDetails = [
      "Est. Id",
      "PO Ref",
      "Customer PO Ref",
      "Customer",
      "Season",
      "Product",
      "Style Name",
      "Style Color",
      "Style Description",
      "Remarks",
      "Fit"
    ];
    const dataOdrDetails = [
      [
        estimation.estId,
        estimation.poRef || "-",
        estimation.customerPoRef || "-",
        estimation.customer?.name,
        estimation.season?.name,
        estimation.product?.name,
        estimation.style,
        estimation.colors,
        estimation.styleDesc,
        estimation.remarks,
        estimation.fit
      ]
    ];

    // Print Style Details
    doc.text("Order Details", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: headerOdrDetails, body: dataOdrDetails });

    // // Print Estimation Summary
    // startY = doc.autoTable.previous.finalY;
    // doc.text(`Estimation Summary`, 7, startY + 7);
    // doc.autoTable({ startY: startY + 10, columns: columnsSummary, body: estimationSummaryPrint });

    const partcolumns1 = [
      { dataKey: "color", title: "Color" },
      { dataKey: "part", title: "Part" },
      { dataKey: "placement", title: "Placement" },
      { dataKey: "fabArticle", title: "Article" },
      { dataKey: "supplier", title: "Supplier", render: value => value?.name },
      { dataKey: "width", title: "Cuttable Width (CM)" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "shrinkage", title: "Shrinkage" },
      { dataKey: "costingCuByCad", title: "Costing CU\nby CAD" },
      { dataKey: "wastage", title: "Wastage (%)" },
      { dataKey: "finalCostingCu", title: "Final Costing\nCU", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "layCu", title: "Ordering CU\nBy CAD" },
      { dataKey: "wastage", title: "Wastage (%)" },
      { dataKey: "orderingCu", title: "Final Ordering CU" },
      { dataKey: "extra", title: "Extra (%)" },
      { dataKey: "fabRequired", title: "Fab. Required" }
    ];

    const partColumns2 = [
      { dataKey: "color", title: "Color" },
      { dataKey: "part", title: "Part" },
      { dataKey: "placement", title: "Placement" },
      { dataKey: "matchingParameter", title: "Matching Parameter" },
      { dataKey: "fabClassification", title: "Fab. Classification" },
      { dataKey: "fabricPatternName", title: "Fab. Pattern" },
      { dataKey: "repeat", title: "Repeats in CM" },
      { dataKey: "fabricCategoryName", title: "Fab. Category" },
      { dataKey: "fabComposition", title: "Fab. Composition" },
      { dataKey: "wash", title: "Wash" },
      { dataKey: "fabDirection", title: "Fab. Direction" }
    ];

    // Print Part Details
    let rows = [...rowsPart];
    rows = rows.filter(e => colorList.includes(e.color));
    rows = rows.map(r => ({
      ...r,
      supplier: r.supplier?.name,
      fabricCategoryName: r.fabricCategory?.name,
      fabricPatternName: r.fabricPattern?.name
    }));

    startY = doc.autoTable.previous.finalY;
    doc.text(`Fabric Requirement`, 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: partcolumns1, body: rows });

    startY = doc.autoTable.previous.finalY;
    doc.autoTable({ startY: startY + 7, columns: partColumns2, body: rows });

    // Print Size Ratio/ Size Breakup
    startY = doc.autoTable.previous.finalY;
    doc.text("Size Breakup", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: columnsSizeBreakup, body: rowsSizeBreakup });

    // Print markers
    startY = doc.autoTable.previous.finalY;
    partList.forEach((part, idx) => {
      const estPart = rowsPart.find(e => e.part === part && colorList.includes(e.color));
      const isManualCutPlan = estPart?.revision?.cutSetting?.cutPlanType && estPart?.revision?.cutSetting?.cutPlanType !== "ALGORITHMIC";
      let columns = [...getColumnsMarker()];
      columns = isManualCutPlan ? columns.filter(c => !["qty", "plyCount"].includes(c.dataKey)) : columns;
      const rows = rowsMarker.filter(p => p.part === part);
      const title = `Markers ${partList.length > 1 ? ` - ${part}` : ""}`;

      if (pageHeight - (startY + 15) < minReqHeight) {
        doc.addPage();
        startY = 10;
      }

      if (rows.length > 0) {
        doc.text(title, 7, startY + 7);
        doc.autoTable({ startY: startY + 10, columns: columns, body: rows });
        startY = doc.autoTable.previous.finalY;
      } else {
        doc.text(`${part} Markers not available.`, 7, startY + 7);
        startY = startY + 10;
      }
    });

    // Print Color Wise Cut Plan
    if (colorList.length > 1) {
      let headerPrinted = false;
      colorList.forEach((color, idx) => {
        const orderQtyMap = rowsSizeBreakup.reduce((acc, sb) => ({ ...acc, [sb.color]: sb.total }), {});

        for (let i = 0; i < partList.length; i++) {
          const part = partList[i];
          const estPart = rowsPart.find(e => e.part === part && color === e.color);
          const rows = rowsMarker
            .filter(m => m.part === part && m.markerType === "Automated")
            .map(m => ({ ...m, plyCount: m.plyMap[color], qty: m.way * m.plyMap[color] }))
            .map(m => ({ ...m, qtyPercent: orderQtyMap[color] ? toFixed((m.qty * 100) / orderQtyMap[color], 2) : "" }))
            .filter(m => m.plyCount > 0);

          if (estPart?.revision?.cutSetting?.cutPlanType !== "ALGORITHMIC" || rows.length === 0) continue;

          const title = `Markers: ${part} - ${color}`;

          if (pageHeight - (startY + 15) < minReqHeight) {
            doc.addPage();
            startY = 10;
          }

          if (!headerPrinted) {
            doc.text("Color-Wise Cut Plan", doc.internal.pageSize.getWidth() / 2, startY + 10, "center");
            startY = startY + 10;
            headerPrinted = true;
          }

          doc.text(title, 7, startY + 7);
          doc.autoTable({ startY: startY + 10, columns: getColumnsMarker(), body: rows });
          startY = doc.autoTable.previous.finalY;
        }
      });
    }
    if (!isEmpty(columnsSummary) && !isEmpty(rowsSummaryMap)) {
      colorList.forEach(color => {
        partList.forEach(part => {
          doc.text(`Estimation Comparison - ${color},${part}`, 7, startY + 7);
          startY = startY + 7;
          doc.autoTable({
            startY: startY + 7,
            columns: columnsSummary,
            body: rowsSummaryMap[`${color}${DEFAULT_SEPARATOR}${part}`]
          });
          startY = doc.autoTable.previous.finalY + 6;
        });
      });
    }

    const filename = `FABRIC ESTIMATION ${estimation.customer?.name} ${estimation.poRef}.pdf`;

    doc.putTotalPages(totalPagesExp);
    doc.save(filename);
  };

  return {
    DialogName,
    estimation,
    columnsPart,
    rowsPart,
    partList,
    flowList,
    flowIdx,
    summaryIdx,
    activeFlow,
    revIdx,
    columnsSummary,
    rowsSummaryMap,
    columnsSizeBreakup,
    rowsSizeBreakup,
    rowsMarker,
    dialogState,
    openFinalizeDialog,
    manuallyFinalizedRevNo,
    choicesRevisions,
    choicesTechSheets,
    onCloseDialog,
    getColumnsMarker,
    onFlowChange,
    setRevIdx,
    setSummaryIdx,
    setDialogState,
    setOpenFinalizeDialog,
    onFinalizeDialogClose,
    onFinalizeSubmit,
    onEstimationUpdate,
    onPartSubmit,
    onSizeBreakupSubmit,
    onMarkerSubmit,
    onFooterActionClick,
    onInlineActionClick,
    updateRevisionSetting,
    onRevision,
    onRevisionDelete,
    onPrint,
    onDownloadTechSheet,
    onDeleteMiniMarker,
    isAttachmentUpload,
    onDrawerSubmit,
    setDrawerState,
    drawerState
  };
};

export default useEstimationView;
